import React, { useState } from 'react';
import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../firebase'; // Adjust path as needed
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Card,
  InputAdornment,
  IconButton,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { EmailOutlined, LockOutlined, Margin, Visibility, VisibilityOff } from '@mui/icons-material';
import logo from '../assets/bouldou white.png'

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const navigate = useNavigate();

  const getErrorMessage = (errorCode) => {
    const errorMessages = {
      'auth/invalid-email': 'Please enter a valid email address',
      'auth/user-disabled': 'This account has been disabled',
      'auth/user-not-found': 'No account found with this email',
      'auth/wrong-password': 'Incorrect password',
      'auth/too-many-requests': 'Too many attempts. Please try again later.',
      'auth/network-request-failed': 'Network error. Please check your connection',
      'auth/invalid-credential': 'Invalid login credentials',
      'auth/email-not-verified': 'Please verify your email before signing in',
      default: 'Login failed. Please try again.',
    };
    return errorMessages[errorCode] || errorMessages['default'];
  };

  const handleSignIn = async () => {
    if (!validateInputs()) return;

    setIsLoading(true);
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      if (!user.emailVerified) {
        await auth.signOut();
        // Throw the specific string so the catch block recognizes it
        throw { code: 'auth/email-not-verified' };
      }

      navigate('/home');
    } catch (error) {
      // Check if the error has a code (Firebase) or use the thrown object
      const errorCode = error.code || 'default';
      setError(getErrorMessage(errorCode));
      setModalVisible(true);
    } finally {
      setIsLoading(false);
    }
  };

  const validateInputs = () => {
    if (!email.trim()) {
      setError('Please enter your email');
      setModalVisible(true);
      return false;
    }

    if (!password.trim()) {
      setError('Please enter your password');
      setModalVisible(true);
      return false;
    }

    return true;
  };

  const handleForgotPassword = async () => {
    if (!email.trim()) {
      setError('Please enter your email to reset password');
      setModalVisible(true);
      return;
    }

    try {
      await sendPasswordResetEmail(auth, email);
      setError('Password reset email sent. Check your inbox!');
      setModalVisible(true);
    } catch (error) {
      setError(getErrorMessage(error.code));
      setModalVisible(true);
    }
  };

  const closeModal = () => {
    setModalVisible(false);
    setError('');
  };

  return (
    <Box sx={styles.container}>
      <Box sx={styles.gradient}>
        <Box sx={styles.content} style={{ marginTop: -50 }}>
          <Box
            component="img"
            src={logo}
            alt="Logo"
            sx={styles.logo}
          />
          <Card sx={styles.card} style={{ marginTop: -20 }}>
            <Typography variant="h5" sx={styles.title}>
              Welcome Back
            </Typography>
            <Typography sx={styles.subtitle}>Sign in to continue</Typography>
            <TextField
              label="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={styles.input}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlined sx={{ color: '#666' }} />
                  </InputAdornment>
                ),
              }}
              inputProps={{ autoCapitalize: 'none', autoCorrect: 'off' }}
              type="email"
            />
            <TextField
              label="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={styles.input}
              fullWidth
              type={showPassword ? 'text' : 'password'}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlined sx={{ color: '#666' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} size="small">
                      {showPassword ? <VisibilityOff sx={{ color: '#666' }} /> : <Visibility sx={{ color: '#666' }} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              inputProps={{ autoCapitalize: 'none' }}
            />
            <Box sx={styles.forgotPassword}>
              <Button onClick={handleForgotPassword} sx={styles.forgotPasswordText}>
                Forgot Password?
              </Button>
            </Box>
            <Button
              variant="contained"
              onClick={handleSignIn}
              sx={styles.signInButton}
              disabled={isLoading}
              disableElevation
            >
              {isLoading ? <CircularProgress size={24} sx={{ color: '#FFF' }} /> : 'Sign In'}
            </Button>
            <Box sx={styles.signUpContainer}>
              <Typography sx={styles.signUpText}>Don't have an account?</Typography>
              <Button onClick={() => navigate('/signup')} sx={styles.signUpLink}>
                Sign Up
              </Button>
            </Box>
          </Card>
        </Box>
        {/* Custom Dialog for Alerts/Errors (Replaces native alert()) */}
        <Dialog
          open={modalVisible}
          onClose={closeModal}
          PaperProps={{ sx: styles.modalContent }}
        >
          <DialogContent>
            <Typography sx={styles.modalText}>{error}</Typography>
          </DialogContent>
          <DialogActions sx={{ p: '0 24px 24px 24px' }}>
            <Button onClick={closeModal} sx={styles.modalButton} fullWidth>
              OK
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #B200BB 0%, #CF003D 100%)', // Adjusted gradient direction for modern look
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '20px',
  },
  gradient: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },

  // === Updated styles.content for horizontal centering ===
  content: {
    // This centers the logo and the card horizontally
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    // ===
    maxWidth: '450px', // Restrict the content width on large screens
    width: '100%',
    px: '24px',
    py: '20px', // Use py for top/bottom padding instead of pt
  },
  logo: {
    width: '150px',
    height: '150px',
    marginBottom: '20px',
    objectFit: 'contain',
  },
  card: {
    backgroundColor: 'rgba(255, 255, 255, 0.95)', // Slightly less transparent
    borderRadius: '20px',
    p: '28px', // Increased padding for better look
    boxShadow: '0 10px 30px rgba(0,0,0,0.15)', // Enhanced shadow
    width: '100%', // Crucial: ensures card fills up to its max width
    maxWidth: '400px' // Redundant here but kept for clarity on card size
  },
  title: {
    fontSize: '28px',
    fontWeight: '700', // Made bold for impact
    color: '#333',
    textAlign: 'center',
    mb: '8px',
  },
  subtitle: {
    fontSize: '18px',
    color: '#666',
    textAlign: 'center',
    mb: '24px',
  },
  input: {
    mb: '16px',
    '& .MuiInputBase-root': {
      backgroundColor: '#FFF',
      borderRadius: '12px',
      border: '1px solid #EEE',
      paddingLeft: '16px', // Use paddingLeft instead of pl
      height: '50px',
    },
    '& .MuiInputBase-input': {
      color: '#333',
    },
    '& .MuiInputLabel-root': {
      top: '-4px', // Adjust label position
    },
    // Fix for floating label when not focused
    '& .MuiInputLabel-shrink': {
      top: '0px',
    },
  },
  forgotPassword: {
    display: 'flex',
    justifyContent: 'flex-end',
    mb: '24px',
  },
  forgotPasswordText: {
    color: 'blue',
    fontSize: '14px',
    textTransform: 'none',
    fontWeight: 'bold',
    '&:hover': {
      backgroundColor: 'transparent',
      textDecoration: 'underline',
    },
  },
  signInButton: {
    background: 'linear-gradient(to right, #C00068, #FF416C)',
    borderRadius: '12px',
    height: 'auto', // Slightly taller button
    mb: '20px',
    textTransform: 'none', // Changed to uppercase for emphasis
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#FFF',
  },
  signUpContainer: {
    display: 'flex',
    justifyContent: 'center',
  },
  signUpText: {
    color: '#666',
    mr: '4px',
  },
  signUpLink: {
    color: 'blue', // Used the secondary gradient color
    fontWeight: 'bold',
    textTransform: 'none',
    marginTop: '-6px',
    '&:hover': {
      backgroundColor: 'transparent',
      textDecoration: 'underline',
    },
  },
  modalContent: {
    borderRadius: '16px',
    p: '20px',
    minWidth: '280px',
  },
  modalText: {
    fontSize: '18px',
    color: '#333',
    textAlign: 'center',
    mb: '16px',
    mt: '8px',
  },
  modalButton: {
    backgroundColor: '#CF003D',
    borderRadius: '12px',
    py: '12px',
    px: '32px',
    color: 'white',
    fontWeight: 'bold',
    fontSize: '16px',
    textTransform: 'none',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: '#A00058',
    },
  },
};

export default Login;
